import { getApiEndpoints } from '@/services/api/endpoints';
import type { IApiService } from '@/services/api/types';
import { createAppAsyncThunk } from '@/store/typedAsyncThunk';
import type {
  RecoveryApiError,
  RecoveryApprovalRevocationRequest,
  RecoveryCancellation,
  RecoveryCancellationRequest,
  RecoveryCaseStatus,
  RecoveryCaseSummary,
  RecoveryCasesPage,
  RecoveryContextReview,
  RecoveryDecisionRequest,
  RecoveryExecutionRequest,
  FetchRecoveryCasesRequest,
  RecoveryCaseDetailPayload,
  RecoveryDecision,
  RecoveryFinalOutcome,
} from './recoveryTypes';
import { parseRecoveryFullEvidence } from './recoveryEvidence';

type ApiError = Error & { status?: number; body?: string };

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

function asNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function asBoolean(value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined;
}

function asStatus(value: unknown): RecoveryCaseStatus | undefined {
  const statuses: RecoveryCaseStatus[] = [
    'EVIDENCE_VERIFIED',
    'PENDING_REVIEW',
    'APPROVED',
    'EXECUTING',
    'OPERATOR_REVIEW_REQUIRED',
    'RESERVATION_RELEASE_PENDING',
    'COMPLETED',
    'REJECTED',
    'CANCELLED',
    'FAILED',
  ];
  return typeof value === 'string' && statuses.includes(value as RecoveryCaseStatus)
    ? (value as RecoveryCaseStatus)
    : undefined;
}

function asStringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === 'string' && Boolean(entry.trim()))
    : [];
}

function asContextReview(value: unknown): RecoveryContextReview | undefined {
  return value === 'PENDING' || value === 'CONSISTENT' || value === 'INSUFFICIENT' ? value : undefined;
}

function sanitizeReviewer(value: unknown): RecoveryDecision['reviewer'] {
  const source = asRecord(value);
  if (!source) return undefined;
  return {
    applicationUserId: asString(source.applicationUserId),
    displayName: asString(source.displayName),
  };
}

function sanitizeDecision(value: unknown): RecoveryDecision | undefined {
  const source = asRecord(value);
  if (!source) return undefined;
  const decision = source.decision === 'APPROVED' || source.decision === 'REJECTED' ? source.decision : undefined;
  return {
    decisionId: asString(source.decisionId),
    decision,
    reviewer: sanitizeReviewer(source.reviewer),
    reason: asString(source.reason),
    caseVersion: asNumber(source.caseVersion),
    decidedAt: asNumber(source.decidedAt),
    revokedAt: asNumber(source.revokedAt),
    revokedBy: sanitizeReviewer(source.revokedBy),
    revocationReason: asString(source.revocationReason),
  };
}

function sanitizeFinalOutcome(value: unknown): RecoveryFinalOutcome | undefined {
  const source = asRecord(value);
  if (!source) return undefined;
  const outcome =
    source.outcome === 'COMPLETED' ||
    source.outcome === 'REJECTED' ||
    source.outcome === 'CANCELLED' ||
    source.outcome === 'FAILED'
      ? source.outcome
      : undefined;
  return {
    outcome,
    recordedAt: asNumber(source.recordedAt),
    reason: asString(source.reason),
  };
}

function sanitizeCancellation(value: unknown): RecoveryCancellation | undefined {
  const source = asRecord(value);
  if (!source) return undefined;
  return {
    decisionId: asString(source.decisionId),
    actorType:
      source.actorType === 'CLAIMANT' || source.actorType === 'SYSTEM_SUPER_ADMIN' ? source.actorType : undefined,
    actor: sanitizeReviewer(source.actor),
    reason: asString(source.reason),
    caseVersion: asNumber(source.caseVersion),
    requestedAt: asNumber(source.requestedAt),
  };
}

function sanitizeSummary(value: unknown, fallbackCaseId?: string): RecoveryCaseSummary | null {
  const source = asRecord(value);
  const caseId = asString(source?.caseId) ?? fallbackCaseId;
  const status = asStatus(source?.status) ?? 'UNKNOWN';
  const version = asNumber(source?.version);
  if (!source || !caseId) return null;

  const proofs = asRecord(source.proofs);
  const support = asRecord(source.support);
  const integrity = asRecord(source.integrity);

  return {
    caseId,
    protocolId: asString(source.protocolId),
    protocolVersion: asNumber(source.protocolVersion),
    policyId: asString(source.policyId),
    allowedAction: asString(source.allowedAction),
    applicationUserId: asString(source.applicationUserId),
    status,
    version: version ?? 0,
    requestedAt: asNumber(source.requestedAt),
    updatedAt: asNumber(source.updatedAt),
    closedAt: asNumber(source.closedAt),
    newPrimaryPhoneHint: asString(source.newPrimaryPhoneHint),
    proofs: {
      existingEmailVerifiedAt: asNumber(proofs?.existingEmailVerifiedAt),
      newPhoneVerifiedAt: asNumber(proofs?.newPhoneVerifiedAt),
      contextSubmittedAt: asNumber(proofs?.contextSubmittedAt),
      emailOutcome: asString(proofs?.emailOutcome),
      phoneOutcome: asString(proofs?.phoneOutcome),
      contextOutcome: asString(proofs?.contextOutcome),
    },
    contextReview: asContextReview(source.contextReview),
    propertyMembershipCount: asNumber(source.propertyMembershipCount),
    organizationMembershipCount: asNumber(source.organizationMembershipCount),
    decision: sanitizeDecision(source.decision),
    failureCode: asString(source.failureCode),
    finalOutcome: sanitizeFinalOutcome(source.finalOutcome),
    canRetryCompletion: asBoolean(source.canRetryCompletion) ?? false,
    canCancel: asBoolean(source.canCancel) ?? false,
    canRetryCancellation: asBoolean(source.canRetryCancellation) ?? false,
    cancellation: sanitizeCancellation(source.cancellation),
    integrity: integrity ? { status: asString(integrity.status), issues: asStringArray(integrity.issues) } : undefined,
    support: support ? { ticketID: asString(support.ticketID) } : undefined,
  };
}

function recoveryError(error: unknown): RecoveryApiError {
  const apiError = typeof error === 'object' && error ? (error as ApiError) : undefined;
  const status = apiError?.status;
  let response: Record<string, unknown> | null = null;
  if (apiError?.body) {
    try {
      response = asRecord(JSON.parse(apiError.body));
    } catch {
      // Invalid or non-JSON error bodies stay inside the secure generic error path.
    }
  }
  const code = asString(response?.code);
  const data = asRecord(response?.data);
  const recovery = asRecord(data?.recovery);
  const details = code
    ? {
        code,
        status: asStatus(data?.status),
        version: asNumber(data?.version),
        failureCode: asString(data?.failureCode),
        canRetryCompletion: asBoolean(data?.canRetryCompletion),
        recovery: recovery
          ? {
              phase: asString(recovery.phase),
              issue: asString(recovery.issue),
              authMutationOccurred:
                typeof recovery.authMutationOccurred === 'boolean' ? recovery.authMutationOccurred : undefined,
              accountLockReleased:
                typeof recovery.accountLockReleased === 'boolean' ? recovery.accountLockReleased : undefined,
              accountLocked: typeof recovery.accountLocked === 'boolean' ? recovery.accountLocked : undefined,
              requiredAction: asString(recovery.requiredAction),
            }
          : undefined,
      }
    : undefined;
  if (status === 401 || status === 403) {
    return { status, code, details, message: 'You no longer have access to recovery cases.' };
  }
  if (status === 404) {
    return { status, code, details, message: 'This recovery case is no longer available.' };
  }
  if (status === 409) {
    if (code === 'RECOVERY_EXECUTION_WON_CONFLICT') {
      return {
        status,
        code,
        details,
        message: 'Recovery execution has already started. Cancellation is no longer available.',
      };
    }
    if (code === 'RECOVERY_RESERVATION_RELEASE_CONFLICT') {
      return {
        status,
        code,
        details,
        message: 'The proposed-phone reservation could not be released safely. Reload the case for operator review.',
      };
    }
    if (code === 'RECOVERY_EXECUTION_RETRY_AVAILABLE') {
      return {
        status,
        code,
        details,
        message:
          'Auth did not complete the approved recovery. Reload the case, then retry completion of the same operation.',
      };
    }
    if (code === 'RECOVERY_EXECUTION_REQUIRES_OPERATOR_REVIEW') {
      return {
        status,
        code,
        details,
        message:
          'Recovery execution requires operator review. Reload the locked case and retry the same completion when the API makes it available.',
      };
    }
    return { status, code, details, message: 'This case changed and must be reviewed again.' };
  }
  if (status === 400 && code === 'INVALID_RECOVERY_CANCELLATION') {
    return { status, code, details, message: 'This cancellation request is invalid. Reload the case and try again.' };
  }
  if (status === 503 && code === 'RECOVERY_AUTH_SERVICE_UNAVAILABLE') {
    return {
      status,
      code,
      details,
      message:
        'The recovery dependency did not confirm completion. Reload the locked case, then retry the same approved completion when available.',
    };
  }
  return { status, code, details, message: 'Recovery cases are temporarily unavailable. Please try again later.' };
}

const sensitiveRequest = { suppressLogging: true } as const;

export const fetchRecoveryCases = createAppAsyncThunk<RecoveryCasesPage, FetchRecoveryCasesRequest | void>(
  'recovery/fetchCases',
  async (request, { extra: { config }, rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (request?.status) params.set('status', request.status);
      if (request?.nextToken) params.set('nextToken', request.nextToken);
      const query = params.toString();
      const endpoint = `${getApiEndpoints().core.recoveryCases.listCases}${query ? `?${query}` : ''}`;
      const response = await config.apiService.get<{ cases?: unknown; nextToken?: unknown }>(
        endpoint,
        sensitiveRequest,
      );
      const cases = Array.isArray(response.cases)
        ? response.cases.flatMap((item) => {
            const summary = sanitizeSummary(item);
            return summary ? [summary] : [];
          })
        : [];
      return {
        cases,
        nextToken: typeof response.nextToken === 'string' && response.nextToken ? response.nextToken : null,
      };
    } catch (error) {
      return rejectWithValue(recoveryError(error));
    }
  },
);

export const fetchRecoveryCase = createAppAsyncThunk<RecoveryCaseDetailPayload, { caseId: string }>(
  'recovery/fetchCase',
  async ({ caseId }, { extra: { config }, rejectWithValue }) => {
    try {
      const endpoint = `${getApiEndpoints().core.recoveryCases.getCase}/${encodeURIComponent(caseId)}`;
      const response = await config.apiService.get<unknown>(endpoint, sensitiveRequest);
      const summary = sanitizeSummary(response, caseId);
      const evidence = parseRecoveryFullEvidence(response);
      if (!summary || !evidence) throw new Error('Invalid recovery case response');
      return { summary, evidence };
    } catch (error) {
      return rejectWithValue(recoveryError(error));
    }
  },
);

async function postDecision(
  action: 'approveCase' | 'rejectCase',
  request: RecoveryDecisionRequest,
  apiService: IApiService,
): Promise<RecoveryCaseSummary> {
  const endpoint = `${getApiEndpoints().core.recoveryCases[action]}/${encodeURIComponent(request.caseId)}/${
    action === 'approveCase' ? 'approve' : 'reject'
  }`;
  const response = await apiService.post<unknown, Omit<RecoveryDecisionRequest, 'caseId'>>(
    endpoint,
    {
      expectedVersion: request.expectedVersion,
      reason: request.reason.trim(),
      contextReview: request.contextReview,
    },
    sensitiveRequest,
  );
  const recoveryCase = sanitizeSummary(response);
  if (!recoveryCase) throw new Error('Invalid recovery decision response');
  return recoveryCase;
}

export const approveRecoveryCase = createAppAsyncThunk<RecoveryCaseSummary, RecoveryDecisionRequest>(
  'recovery/approve',
  async (request, { extra: { config }, rejectWithValue }) => {
    try {
      return await postDecision('approveCase', request, config.apiService);
    } catch (error) {
      return rejectWithValue(recoveryError(error));
    }
  },
);

export const rejectRecoveryCase = createAppAsyncThunk<RecoveryCaseSummary, RecoveryDecisionRequest>(
  'recovery/reject',
  async (request, { extra: { config }, rejectWithValue }) => {
    try {
      return await postDecision('rejectCase', request, config.apiService);
    } catch (error) {
      return rejectWithValue(recoveryError(error));
    }
  },
);

export const revokeRecoveryCaseApproval = createAppAsyncThunk<RecoveryCaseSummary, RecoveryApprovalRevocationRequest>(
  'recovery/revokeApproval',
  async ({ caseId, expectedVersion, message }, { extra: { config }, rejectWithValue }) => {
    try {
      const endpoint = `${getApiEndpoints().core.recoveryCases.revokeApproval}/${encodeURIComponent(caseId)}/revoke-approval`;
      const response = await config.apiService.post<unknown, { expectedVersion: number; message: string }>(
        endpoint,
        { expectedVersion, message: message.trim() },
        sensitiveRequest,
      );
      const recoveryCase = sanitizeSummary(response);
      if (!recoveryCase) throw new Error('Invalid recovery approval revocation response');
      return recoveryCase;
    } catch (error) {
      return rejectWithValue(recoveryError(error));
    }
  },
);

export const cancelRecoveryCase = createAppAsyncThunk<RecoveryCaseSummary, RecoveryCancellationRequest>(
  'recovery/cancel',
  async ({ caseId, expectedVersion, reason }, { extra: { config }, rejectWithValue }) => {
    try {
      const endpoint = `${getApiEndpoints().core.recoveryCases.cancelCase}/${encodeURIComponent(caseId)}/cancel`;
      const response = await config.apiService.post<unknown, { expectedVersion: number; reason: string }>(
        endpoint,
        { expectedVersion, reason: reason.trim() },
        sensitiveRequest,
      );
      const recoveryCase = sanitizeSummary(response, caseId);
      if (!recoveryCase) throw new Error('Invalid recovery cancellation response');
      return recoveryCase;
    } catch (error) {
      return rejectWithValue(recoveryError(error));
    }
  },
);

export const executeRecoveryCase = createAppAsyncThunk<RecoveryCaseSummary, RecoveryExecutionRequest>(
  'recovery/execute',
  async ({ caseId, expectedVersion }, { extra: { config }, rejectWithValue }) => {
    try {
      const endpoint = `${getApiEndpoints().core.recoveryCases.executeCase}/${encodeURIComponent(caseId)}/execute`;
      const response = await config.apiService.post<unknown, { expectedVersion: number }>(
        endpoint,
        { expectedVersion },
        sensitiveRequest,
      );
      const recoveryCase = sanitizeSummary(response);
      if (!recoveryCase) throw new Error('Invalid recovery execution response');
      return recoveryCase;
    } catch (error) {
      return rejectWithValue(recoveryError(error));
    }
  },
);
